import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import { Lock as LockIcon, Add as AddIcon, PersonAdd as PersonAddIcon, Clear as ClearIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import LockersMenRoomLayout, { normalizeMenRoomSlotKey, type MenRoomLockerRow } from './LockersMenRoomLayout';
import LockersWomenRoomLayout, { normalizeWomenRoomSlotKey } from './LockersWomenRoomLayout';
import LockersAdministrationLayout, { normalizeAdministrationSlotKey } from './LockersAdministrationLayout';

type Section = 'women' | 'men' | 'administration';

interface Assignee {
  id: string;
  firstName: string;
  lastName: string;
  department?: string | null;
}

interface LockerRow {
  id: string;
  section: Section;
  slotLabel: string;
  userId: string | null;
  externalOccupantLabel?: string | null;
  assignee?: Assignee | null;
}

interface AssignableUser {
  id: string;
  firstName: string;
  lastName: string;
  department?: string | null;
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

const Lockers: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('lockers.manage');

  const [tab, setTab] = useState(0);
  const [lockers, setLockers] = useState<LockerRow[]>([]);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addSection, setAddSection] = useState<Section>('women');
  const [addLabel, setAddLabel] = useState('');
  const [addAssignUserId, setAddAssignUserId] = useState('');
  const [addExternalLabel, setAddExternalLabel] = useState('');
  const [addSaving, setAddSaving] = useState(false);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignLocker, setAssignLocker] = useState<LockerRow | null>(null);
  const [assignUserId, setAssignUserId] = useState('');
  const [assignExternalLabel, setAssignExternalLabel] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);

  const section: Section = tab === 0 ? 'women' : tab === 1 ? 'men' : 'administration';

  const fetchLockers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(getApiUrl('/lockers'), { headers: authHeaders() });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to load lockers');
      }
      const data = await res.json();
      setLockers(data.lockers || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load lockers');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAssignable = useCallback(async () => {
    if (!canManage) return;
    try {
      const res = await fetch(getApiUrl('/lockers/assignable-users'), { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setAssignableUsers(data.users || []);
    } catch {
      /* ignore */
    }
  }, [canManage]);

  useEffect(() => {
    fetchLockers();
  }, [fetchLockers]);

  useEffect(() => {
    fetchAssignable();
  }, [fetchAssignable]);

  /** Women/men only: one personal locker per user across those sections */
  const personalOccupiedUserIds = useMemo(() => {
    const ids = new Set<string>();
    lockers.forEach((l) => {
      if ((l.section === 'women' || l.section === 'men') && l.userId) ids.add(l.userId);
    });
    return ids;
  }, [lockers]);

  const filteredLockers = useMemo(
    () => lockers.filter((l) => l.section === section).sort((a, b) => a.slotLabel.localeCompare(b.slotLabel, undefined, { numeric: true })),
    [lockers, section]
  );

  /** Men's room diagram: canonical keys "1".."22" only */
  const menLockersByLabel = useMemo(() => {
    const m = new Map<string, LockerRow>();
    lockers
      .filter((l) => l.section === 'men')
      .forEach((l) => {
        const k = normalizeMenRoomSlotKey(l.slotLabel);
        if (k) m.set(k, l);
      });
    return m;
  }, [lockers]);

  /** Women's room diagram: canonical keys "1".."27" only */
  const womenLockersByLabel = useMemo(() => {
    const m = new Map<string, LockerRow>();
    lockers
      .filter((l) => l.section === 'women')
      .forEach((l) => {
        const k = normalizeWomenRoomSlotKey(l.slotLabel);
        if (k) m.set(k, l);
      });
    return m;
  }, [lockers]);

  const administrationLockersByLabel = useMemo(() => {
    const m = new Map<string, LockerRow>();
    lockers
      .filter((l) => l.section === 'administration')
      .forEach((l) => {
        const k = normalizeAdministrationSlotKey(l.slotLabel);
        if (k) m.set(k, l);
      });
    return m;
  }, [lockers]);

  const usersForAssignSelect = useMemo(() => {
    if (!assignLocker) return assignableUsers;
    if (assignLocker.section === 'administration') return assignableUsers;
    return assignableUsers.filter(
      (u) => !personalOccupiedUserIds.has(u.id) || u.id === assignLocker.userId
    );
  }, [assignableUsers, assignLocker, personalOccupiedUserIds]);

  /** Workers not already on a women/men locker (add flow); administration allows anyone */
  const usersForAddAssignSelect = useMemo(() => {
    if (addSection === 'administration') return assignableUsers;
    return assignableUsers.filter((u) => !personalOccupiedUserIds.has(u.id));
  }, [assignableUsers, addSection, personalOccupiedUserIds]);

  const openAssign = (row: LockerRow) => {
    setAssignLocker(row);
    setAssignUserId(row.userId || '');
    setAssignExternalLabel(row.externalOccupantLabel?.trim() ? row.externalOccupantLabel : '');
    setAssignOpen(true);
  };

  const lockerIsOccupied = (row: LockerRow) =>
    !!(row.userId || (row.externalOccupantLabel && row.externalOccupantLabel.trim()));

  const handleMenLayoutSlot = (row: MenRoomLockerRow | null, canonicalLabel: string) => {
    if (row) {
      openAssign(row as LockerRow);
    } else if (canManage) {
      setAddAssignUserId('');
      setAddExternalLabel('');
      setAddSection('men');
      setAddLabel(canonicalLabel);
      setAddOpen(true);
    }
  };

  const handleWomenLayoutSlot = (row: MenRoomLockerRow | null, canonicalLabel: string) => {
    if (row) {
      openAssign(row as LockerRow);
    } else if (canManage) {
      setAddAssignUserId('');
      setAddExternalLabel('');
      setAddSection('women');
      setAddLabel(canonicalLabel);
      setAddOpen(true);
    }
  };

  const handleAdministrationLayoutSlot = (row: MenRoomLockerRow | null, canonicalLabel: string) => {
    if (row) {
      openAssign(row as LockerRow);
    } else if (canManage) {
      setAddAssignUserId('');
      setAddExternalLabel('');
      setAddSection('administration');
      setAddLabel(canonicalLabel);
      setAddOpen(true);
    }
  };

  const openAddDialog = () => {
    setAddAssignUserId('');
    setAddExternalLabel('');
    setAddSection(section);
    setAddOpen(true);
  };

  const handleAdd = async () => {
    const label = addLabel.trim();
    if (!label) return;
    setAddSaving(true);
    try {
      const res = await fetch(getApiUrl('/lockers'), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ section: addSection, slotLabel: label })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to add locker');
      const newId = data.locker?.id as string | undefined;
      if (newId && addAssignUserId) {
        const patchRes = await fetch(getApiUrl(`/lockers/${newId}`), {
          method: 'PATCH',
          headers: authHeaders(),
          body: JSON.stringify({ userId: addAssignUserId, externalOccupantLabel: null })
        });
        const patchData = await patchRes.json().catch(() => ({}));
        if (!patchRes.ok) throw new Error(patchData.error || 'Locker created but assignment failed');
      } else if (newId) {
        const ext = addExternalLabel.trim();
        if (ext) {
          const patchRes = await fetch(getApiUrl(`/lockers/${newId}`), {
            method: 'PATCH',
            headers: authHeaders(),
            body: JSON.stringify({ userId: null, externalOccupantLabel: ext.slice(0, 255) })
          });
          const patchData = await patchRes.json().catch(() => ({}));
          if (!patchRes.ok) throw new Error(patchData.error || 'Locker created but external label failed');
        }
      }
      setAddOpen(false);
      setAddLabel('');
      setAddAssignUserId('');
      setAddExternalLabel('');
      await fetchLockers();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add locker');
    } finally {
      setAddSaving(false);
    }
  };

  const handleAssignSave = async () => {
    if (!assignLocker) return;
    setAssignSaving(true);
    try {
      const extTrim = assignExternalLabel.trim();
      const body =
        assignUserId.trim() !== ''
          ? { userId: assignUserId, externalOccupantLabel: null }
          : extTrim !== ''
            ? { userId: null, externalOccupantLabel: extTrim.slice(0, 255) }
            : { userId: null, externalOccupantLabel: null };
      const res = await fetch(getApiUrl(`/lockers/${assignLocker.id}`), {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update assignment');
      setAssignOpen(false);
      setAssignLocker(null);
      await fetchLockers();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update assignment');
    } finally {
      setAssignSaving(false);
    }
  };

  const handleClear = async (row: LockerRow) => {
    try {
      const res = await fetch(getApiUrl(`/lockers/${row.id}`), {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ userId: null, externalOccupantLabel: null })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to clear');
      await fetchLockers();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to clear');
    }
  };

  const handleDelete = async (row: LockerRow) => {
    if (lockerIsOccupied(row)) return;
    if (!window.confirm(t('lockers.confirmDelete', 'Delete this empty locker slot?'))) return;
    try {
      const res = await fetch(getApiUrl(`/lockers/${row.id}`), { method: 'DELETE', headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to delete');
      await fetchLockers();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  const formatUser = (u: Assignee | AssignableUser) =>
    `${u.firstName} ${u.lastName}${u.department ? ` (${u.department})` : ''}`;

  const displayOccupant = (row: LockerRow) => {
    if (row.assignee) return formatUser(row.assignee);
    const ext = row.externalOccupantLabel?.trim();
    if (ext) return ext;
    return <em>{t('lockers.free', 'Free')}</em>;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LockIcon />
        {t('lockers.title', 'Personal lockers')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(
          'lockers.subtitle',
          'Women and men: one personal locker per person across those sections. Administration: the same person may have several slots.'
        )}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={t('lockers.sectionWomen', 'Women')} />
          <Tab label={t('lockers.sectionMen', 'Men')} />
          <Tab label={t('lockers.sectionAdministration', 'Administration')} />
        </Tabs>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog}>
            {t('lockers.addSlot', 'Add locker')}
          </Button>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {section === 'women' && (
            <LockersWomenRoomLayout
              lockersByLabel={womenLockersByLabel}
              canManage={canManage}
              onSelectSlot={handleWomenLayoutSlot}
            />
          )}
          {section === 'men' && (
            <LockersMenRoomLayout
              lockersByLabel={menLockersByLabel}
              canManage={canManage}
              onSelectSlot={handleMenLayoutSlot}
            />
          )}
          {section === 'administration' && (
            <LockersAdministrationLayout
              lockersByLabel={administrationLockersByLabel}
              canManage={canManage}
              onSelectSlot={handleAdministrationLayoutSlot}
            />
          )}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('lockers.columnSlot', 'Slot')}</TableCell>
                <TableCell>{t('lockers.columnAssignee', 'Assignee')}</TableCell>
                {canManage && <TableCell align="right">{t('lockers.columnActions', 'Actions')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLockers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 3 : 2}>
                    <Typography color="text.secondary">{t('lockers.noSlots', 'No lockers in this section yet.')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredLockers.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.slotLabel}</TableCell>
                    <TableCell>{displayOccupant(row)}</TableCell>
                    {canManage && (
                      <TableCell align="right">
                        <Tooltip title={t('lockers.assign', 'Assign')}>
                          <IconButton size="small" onClick={() => openAssign(row)} color="primary">
                            <PersonAddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {lockerIsOccupied(row) && (
                          <Tooltip title={t('lockers.clear', 'Clear')}>
                            <IconButton size="small" onClick={() => handleClear(row)} color="warning">
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!lockerIsOccupied(row) && (
                          <Tooltip title={t('lockers.deleteSlot', 'Delete slot')}>
                            <IconButton size="small" onClick={() => handleDelete(row)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}

      <Dialog
        open={addOpen}
        onClose={() => {
          if (!addSaving) {
            setAddOpen(false);
            setAddAssignUserId('');
            setAddExternalLabel('');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('lockers.addSlot', 'Add locker')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>{t('lockers.section', 'Section')}</InputLabel>
            <Select
              value={addSection}
              label={t('lockers.section', 'Section')}
              onChange={(e) => setAddSection(e.target.value as Section)}
            >
              <MenuItem value="women">{t('lockers.sectionWomen', 'Women')}</MenuItem>
              <MenuItem value="men">{t('lockers.sectionMen', 'Men')}</MenuItem>
              <MenuItem value="administration">{t('lockers.sectionAdministration', 'Administration')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={t('lockers.slotLabel', 'Label / number')}
            value={addLabel}
            onChange={(e) => setAddLabel(e.target.value)}
            fullWidth
            placeholder="12, A-3, …"
          />
          <TextField
            label={t('lockers.externalOccupantLabel', 'Partner / external name')}
            value={addExternalLabel}
            onChange={(e) => {
              setAddExternalLabel(e.target.value);
              if (e.target.value.trim()) setAddAssignUserId('');
            }}
            fullWidth
            inputProps={{ maxLength: 255 }}
            helperText={t('lockers.externalMutuallyExclusive', 'Either a worker or this name — not both.')}
          />
          <FormControl fullWidth size="small">
            <InputLabel>{t('lockers.addOptionalAssign', 'Assign worker (optional)')}</InputLabel>
            <Select
              value={addAssignUserId}
              label={t('lockers.addOptionalAssign', 'Assign worker (optional)')}
              onChange={(e) => {
                setAddAssignUserId(e.target.value);
                if (e.target.value) setAddExternalLabel('');
              }}
            >
              <MenuItem value="">
                <em>{t('lockers.addSkipAssign', 'Skip — assign later')}</em>
              </MenuItem>
              {usersForAddAssignSelect.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {formatUser(u)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            {t('lockers.addOptionalAssignHint', 'After saving, the locker appears on the map; you can assign or change the worker anytime.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddOpen(false);
              setAddAssignUserId('');
              setAddExternalLabel('');
            }}
            disabled={addSaving}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleAdd} variant="contained" disabled={addSaving || !addLabel.trim()}>
            {t('lockers.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignOpen}
        onClose={() => {
          if (!assignSaving) {
            setAssignOpen(false);
            setAssignExternalLabel('');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('lockers.assignTitle', 'Assign locker')} {assignLocker ? `— ${assignLocker.slotLabel}` : ''}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('lockers.selectWorker', 'Worker')}</InputLabel>
            <Select
              value={assignUserId}
              label={t('lockers.selectWorker', 'Worker')}
              onChange={(e) => {
                setAssignUserId(e.target.value);
                if (e.target.value) setAssignExternalLabel('');
              }}
            >
              <MenuItem value="">
                <em>{t('lockers.noAssignee', 'None (free)')}</em>
              </MenuItem>
              {usersForAssignSelect.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {formatUser(u)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('lockers.externalOccupantLabel', 'Partner / external name')}
            value={assignExternalLabel}
            onChange={(e) => {
              setAssignExternalLabel(e.target.value);
              if (e.target.value.trim()) setAssignUserId('');
            }}
            fullWidth
            inputProps={{ maxLength: 255 }}
            helperText={t('lockers.externalMutuallyExclusive', 'Either a worker or this name — not both.')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)} disabled={assignSaving}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleAssignSave} variant="contained" disabled={assignSaving}>
            {t('lockers.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Lockers;
